import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers";
import PageLayout from "../components/layout/PageLayout";
import { useReactToPrint } from "react-to-print";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import Loader from "components/lists/Loader";
import CustomNumberInput from "components/CustomNumberInput";
import { dateISOFormat, dateToMySQLFormat } from "helpers";
import {
  DATE_VIEWS,
  MOVEMENT_TYPES,
  MovementTypes,
  Roles,
  STATUS_CODES,
  defaultMovement,
} from "../constants";
import ArticlesList from "components/Operations/ArticlesList";
import { Article, AssociatedClient, Movement, MovementAdditionalItemDraft, MovementArticle } from "types";
import { MOVEMENTS_NEXT_ADJUSTMENT_DOC_URL, MOVEMENTS_URL } from "constants/urls";
import { useConfirmationModal } from "hooks/useConfirmationModal";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import FileImport from "components/FileImport/FileImport";
import { PrintLayout } from "components/Prints/PrintLayout";
import OperationPrintHeaderDetails from "components/Prints/Operations/OperationPrintHeaderDetails";
import OperationPrintDetails from "components/Prints/Operations/OperationPrintDetails";
import { decimalFormat } from "helpers/formatNumber";
import { ENTITY } from "constants/entities";
import { calculateMovementTotals, initMovementDate } from "helpers/movement";
import { OperationPrintTotals } from "components/Prints/Operations/OperationPrintTotal";
import { hydrateDraftMovementArticles } from "helpers/movementDraftLineData";
import MovementAdditionalItemsStepModal, {
  MovementAdditionalItemsConfirmPayload,
} from "components/Operations/MovementAdditionalItemsStepModal";
import {
  buildRequiredAdditionalItemDrafts,
  buildAdditionalMovementsPayload,
  getAdditionalItemsModalTitle,
  getMissingRequiredAdditionalItems,
} from "helpers/additionalMovements";

export default function IncomePage() {
  const { movement_draft_id } = useParams();
  const [movement, setMovement] = useState<Movement>({
    ...defaultMovement,
    movement_type_id: MovementTypes.Income,
    movement_draft_id: movement_draft_id
      ? Number(movement_draft_id)
      : undefined,
    isAdjustment: false,
    movement_id: null,
  });
  const [movementToPrint, setMovementToPrint] = useState<Movement | null>(null);
  const [additionalMovementsToPrint, setAdditionalMovementsToPrint] = useState<Movement[]>([]);
  const [movementLoading, setMovementLoading] = useState<boolean>(false);
  const [additionalItemsModalOpen, setAdditionalItemsModalOpen] = useState(false);
  const [additionalItemsInitialItems, setAdditionalItemsInitialItems] =
    useState<MovementAdditionalItemDraft[]>([]);
  const [clientAssociatedClients, setClientAssociatedClients] = useState<
    AssociatedClient[]
  >([]);
  const [clientArticles, setClientArticles] = useState<Article[]>([]);
  const [articleRowIndex, setArticleRowIndex] = useState<number>(0);
  const printRef = useRef(null);
  const navigate = useNavigate();
  const { showSnackBar } = useToast();
  const { showModal } = useConfirmationModal();
  const {
    firstLoad: onMovementFirstLoad,
    createData: createMovement,
    fetchData: getMovement,
  } = useCrud(MOVEMENTS_URL);
  const { fetchData: fetchMovements } = useCrud(MOVEMENTS_URL);
  const [availableMovements, setAvailableMovements] = useState<Movement[]>([]);

  const {
    userData,
    stores,
    clients,
    associatedClients,
    additionalItems,
    articles,
    users,
    locations,
    sublocations,
    updateSessionData,
  } = useSession();

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === movement.client_id) ?? null,
    [clients, movement.client_id]
  );

  const scopedClientIds = useMemo(
    () => [
      movement.client_id,
      ...clients
        .filter((client) => client.parent_client_id === movement.client_id)
        .map((client) => client.id),
    ],
    [clients, movement.client_id],
  );

  const childClients = useMemo(
    () =>
      clients.filter(
        (client) => client.parent_client_id === movement.client_id,
      ),
    [clients, movement.client_id],
  );

  const clientAdditionalItems = useMemo(
    () =>
      additionalItems.filter(
        (item) =>
          item.client_id == null || scopedClientIds.includes(item.client_id),
      ),
    [additionalItems, scopedClientIds],
  );

  const movementTotals = useMemo(
    () => calculateMovementTotals(movement),
    [movement]
  );

  const { fetchData: fetchNextAdjustmentDoc } = useCrud<{
    client_document: string;
  }>(MOVEMENTS_NEXT_ADJUSTMENT_DOC_URL, { disableAutoFetch: true });

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      if (additionalMovementsToPrint.length > 0) {
        const [nextMovementToPrint, ...remainingMovementsToPrint] =
          additionalMovementsToPrint;

        setAdditionalMovementsToPrint(remainingMovementsToPrint);
        setMovementToPrint(nextMovementToPrint);
        return;
      }

      navigate("/operations");
      setMovementLoading(false);
      showSnackBar("Se ha creado el ingreso correctamente", "success");
    },
  });

  const handleChange = useCallback((name: string, value: any) => {
    setMovement((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmit = async (
    print: boolean = true,
    additionalItemsToCreate: MovementAdditionalItemDraft[] = [],
    additionalMovementHeader?: Movement,
  ) => {
    setMovementLoading(true);

    const movementResponse = await createMovement(
      [
        {
          ...movement,
          additional_items: print ? [] : movement.additional_items,
          movement_type_id: print
            ? movement.movement_type_id
            : MovementTypes.IncomeDraft,
          movement_draft_id: movement_draft_id
            ? Number(movement_draft_id)
            : undefined,
        },
      ],
      true
    );

    if (movementResponse.status === STATUS_CODES.OK) {
      const aggregatedMovementResponse = await getMovement(
        `${movementResponse.data[0].id}`,
        true
      );

      const createdMovement = aggregatedMovementResponse.data[0];

    if (print && additionalItemsToCreate.length > 0) {
      const additionalMovementsPayload = buildAdditionalMovementsPayload({
        baseMovement: movement,
        headerMovement: additionalMovementHeader,
        items: additionalItemsToCreate,
        originMovementId: createdMovement.id,
      });

      const additionalMovementResponse = await createMovement(
        additionalMovementsPayload,
        true,
      );

      if (additionalMovementResponse.status !== STATUS_CODES.OK) {
        setMovementLoading(false);
        showSnackBar(
          additionalMovementResponse.data?.message ||
            "El ingreso se creó, pero no se pudieron crear los adicionales asociados.",
          "error",
        );
        return;
      }

      if (Array.isArray(additionalMovementResponse.data)) {
        setAdditionalMovementsToPrint(additionalMovementResponse.data);
      }
    }

      if (createdMovement?.articles?.length > 0) {
        createdMovement.articles.forEach((ma: MovementArticle) => {
          const baseArticle = ma.article_locations.article;
          const sessionArticle = articles.find((a) => a.id === baseArticle.id);

          updateSessionData(ENTITY.ARTICLES, {
            ...baseArticle,
            amount: (sessionArticle?.amount ?? 0) + (ma.amount ?? 0),
          });
        });
      }

      if (print) {
        setMovementToPrint(createdMovement);
      } else {
        navigate("/operations");
        setMovementLoading(false);
        showSnackBar(
          "Se ha creado el borrrador del ingreso correctamente.",
          "success"
        );
      }
    } else {
      const responseData = movementResponse.data as { message?: unknown } | null;
      const apiErrorMessage =
        typeof responseData?.message === "string" ? responseData.message : "";

      setMovementLoading(false);
      showSnackBar(
        apiErrorMessage ||
          (movementResponse.status === STATUS_CODES.BAD_REQUEST
            ? "El número de remito ya se encuentra registrado, por favor modifiquelo e intente nuevamente."
            : "El ingreso no ha podido crearse, por favor intente nuevamente."),
        "error"
      );
    }
  };

  const handleConfirmAdditionalItems = ({
    movement: additionalMovementHeader,
    items,
  }: MovementAdditionalItemsConfirmPayload) => {
    const missingRequiredItems = getMissingRequiredAdditionalItems({
      movement,
      clients,
      additionalItems,
      selectedItems: items,
      movementType: "income",
    });

    if (missingRequiredItems.length > 0) {
      showModal({
        title: "Adicional obligatorio",
        message:
          missingRequiredItems.length === 1
            ? `El adicional "${missingRequiredItems[0].additionalItem.description}" es obligatorio para este ingreso.`
            : `Hay ${missingRequiredItems.length} adicionales obligatorios pendientes para este ingreso: ${missingRequiredItems
                .map((item) => item.additionalItem.description)
                .join(", ")}.`,
        confirmLabel:
          missingRequiredItems.length === 1 ? "Aplicar" : "Aplicar todos",
        action: async () => {
          const requiredItems =
            buildRequiredAdditionalItemDrafts(missingRequiredItems);

          setAdditionalItemsInitialItems([...items, ...requiredItems]);
        },
      });

      return;
    }

    setAdditionalItemsModalOpen(false);
    setAdditionalItemsInitialItems([]);
    handleSubmit(true, items, additionalMovementHeader);
  };

  const calculateTotalPallets = useCallback(() => {
    const totalPallets = movement.articles.reduce((sum, article) => {
      const unitsPerPallet = article.article_locations.article.units_per_pallet;
      const amount = article.amount;
      const palletsForArticle = unitsPerPallet
        ? Math.ceil(amount / unitsPerPallet)
        : 0;
      return sum + palletsForArticle;
    }, 0);
    setMovement((prev) => ({ ...prev, pallets: decimalFormat(totalPallets) }));
  }, [movement.articles]);

  const handleConfirmSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    showModal({
      title: "Confirmar",
      message: "¿Está seguro que desea confirmar el ingreso?",
      action: async () => {
        setAdditionalItemsInitialItems([]);
        setAdditionalItemsModalOpen(true);
      },
    });
  };

  const submitDisabled = useMemo(() => !movement.weight, [movement]);

  const filteredUsers = useMemo(
    () =>
      users?.map((u) => ({
        id: u.id,
        label: `${u.first_name} ${u.last_name}`,
      })),
    [users]
  );

  const storesList = useMemo(
    () => [
      { id: 0, label: "Todos" },
      ...stores
        .filter((s) => s.is_active)
        .map((s) => ({
          id: s.id,
          label: s.description,
        })),
    ],
    [stores]
  );

  const selectedWarehouse = useMemo(() => {
    if (movement.store_id === null) {
      return { id: 0, label: "Todos" };
    }

    const store = stores.find(
      (s) => s.is_active && s.id === movement.store_id
    );

    return store
      ? { id: store.id, label: store.description }
      : null;
  }, [stores, movement.store_id]);

  useEffect(() => {
    if (movementToPrint) {
      handlePrint();
    }
  }, [movementToPrint, handlePrint]);

  useEffect(() => {
    if (movement.client_id !== 0) {
      setClientAssociatedClients(
        associatedClients.filter(
          (ac) => ac.client_id === movement.client_id && ac.is_active
        )
      );
      setClientArticles(
        articles.filter((article) => scopedClientIds.includes(article.client_id))
      );
    }
  }, [movement.client_id, associatedClients, articles, scopedClientIds]);

  useEffect(() => {
    if (userData) {
      handleChange("created_by", userData.id);
    }
  }, [userData, handleChange]);

  useEffect(() => {
    calculateTotalPallets();
  }, [movement.articles, calculateTotalPallets]);

  const handleSendDraft = () => {
    handleSubmit(false);
  };

  useEffect(() => {
    const getMovementDraft = async (draftId: number) => {
      setMovementLoading(true);
      const draftMovementResponse = await getMovement(draftId.toString(), true);
      if (
        draftMovementResponse.status === 200 &&
        draftMovementResponse.data.length > 0 &&
        draftMovementResponse.data[0] !== null
      ) {
        const draftMovement = draftMovementResponse.data[0];
        setMovement({
          ...defaultMovement,
          client_id: draftMovement.client_id,
          created_by: userData ? userData.id : defaultMovement.created_by,
          store_id: draftMovement.store_id,
          updated_by: draftMovement.updated_by,
          associated_client: draftMovement.associated_client,
          client_document: draftMovement.client_document,
          pallets: draftMovement.pallets,
          weight: draftMovement.weight,
          numero_remito: draftMovement.numero_remito,
          observation: draftMovement.observation,
          date: dateToMySQLFormat(draftMovement.date),
          articles: hydrateDraftMovementArticles(draftMovement.articles || []),
          additional_items: draftMovement.additional_items || [],
        });

        setArticleRowIndex(draftMovement.articles.length);
      }
      setMovementLoading(false);
    };
    if (movement.movement_draft_id) {
      getMovementDraft(movement.movement_draft_id);
    }
  }, [movement.movement_draft_id]);

  useEffect(() => {
    const loadMovements = async () => {
      if (movement.isAdjustment && movement.client_id && movement.associated_client) {
        const query = `?client_id=${movement.client_id}&movement_type_id=${movement.movement_type_id}&associatedClient=${encodeURIComponent(movement.associated_client)}`;
        const res = await fetchMovements(query, true);
        const movements = res.data?.result ?? [];
        setAvailableMovements(Array.isArray(movements) ? movements : []);
      } else {
        setAvailableMovements([]);
      }
    };

    loadMovements();
  }, [
    movement.isAdjustment,
    movement.client_id,
    movement.associated_client,
    movement.movement_type_id,
  ]);

  useEffect(() => {
    initMovementDate(movement.date, handleChange, { setSettlement: true });
  }, []);

  useEffect(() => {
    const loadNextAdjustmentDocument = async () => {
      if (!movement.isAdjustment) return;
      if (!movement.movement_id) return;

      const response = await fetchNextAdjustmentDoc(
        `${movement.movement_id}`,
        true
      );

      if (response.status !== STATUS_CODES.OK) return;

      setMovement((prev) => ({
        ...prev,
        client_document: response.data.client_document,
      }));
    };

    loadNextAdjustmentDocument();
  }, [movement.isAdjustment, movement.movement_id]);

  return (
    <PageLayout
      pageTitle="Ingresos"
      pageDesc="Orden de ingreso de mercadería"
      importButton={
        <FileImport
          movementType={MOVEMENT_TYPES.INCOME.label}
          locations={locations}
          clients={clients}
          associatedClients={associatedClients}
          articles={articles}
          additional_items={additionalItems}
          users={users}
          stores={stores}
        />
      }
    >
      {movementToPrint && (
        <PrintLayout
          ref={printRef}
          headerInfo={
            <OperationPrintHeaderDetails
              movement={movementToPrint}
              stores={stores}
            />
          }
          footerInfo={<OperationPrintTotals movement={movement} />}
        >
          <OperationPrintDetails
            users={users}
            movement={movementToPrint}
            client={movementToPrint.client || null}
          />
        </PrintLayout>
      )}
      <form onSubmit={handleConfirmSubmit}>
        {movementLoading ? (
          <Loader isLoading={true} isFirstLoad={onMovementFirstLoad} />
        ) : (
          <Grid container columnSpacing={1}>
            {userData?.role === Roles.SUPERADMIN && (
              <Grid
                item xs={4}
                sx={{ display: "flex", gap: 1, alignItems: "center"}}
              >
                <Typography variant="body2" fontWeight="bold">
                  Movimiento
                </Typography>

                <Switch
                  data-cy="adjustment-switch"
                  checked={movement.isAdjustment ?? false}
                  onChange={(e) => {
                    handleChange("isAdjustment", e.target.checked);
                    if (!e.target.checked) handleChange("movement_id", null);
                  }}
                  disabled={!!movement.movement_draft_id}
                />

                <Typography variant="body2" fontWeight="bold">
                  Ajuste de inventario
                </Typography>
              </Grid>
            )}
            <Grid item xs={4}>
              {userData?.role === Roles.SUPERADMIN && movement.isAdjustment && (
                <FormControl fullWidth margin="none" sx={{ maxWidth: 400 }}>
                  <Autocomplete
                    data-cy="adjustment-movement"
                    id="adjustment-movement"
                    autoComplete
                    options={availableMovements.map((m) => ({
                      id: m.id,
                      date: m.date,
                      label: `#${m.id} - ${new Date(m.date).toLocaleDateString()} - ${m.client_document || "s/doc. del cliente"}`,
                    }))}
                    value={
                      movement.movement_id
                        ? {
                            id: movement.movement_id,
                            date: availableMovements.find((m) => m.id === movement.movement_id)?.date ?? movement.date,
                            label: `Movimiento #${movement.movement_id}`,
                          }
                        : null
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(e, value) => {
                      handleChange("movement_id", value?.id ?? null);
                      if (value?.date) {
                        const originDate = dateISOFormat(value.date);
                        handleChange("date", originDate);
                        handleChange("settlement_date", originDate);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Movimiento principal" />
                    )}
                  />
                </FormControl>
              )}
            </Grid>
            <Grid item xs={4}>
              {movement.isAdjustment && (!movement.client_id || !movement.associated_client) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  ⚠️ Seleccione cliente y cliente asociado para ver los movimientos disponibles.
                </Typography>
              )}
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  id="user"
                  autoComplete
                  disabled
                  value={{
                    id: movement.created_by,
                    label: `${
                      users.find((u) => u.id === movement.created_by)
                        ?.first_name ?? ""
                    } ${
                      users.find((u) => u.id === movement.created_by)
                        ?.last_name ?? ""
                    }`,
                  }}
                  options={filteredUsers}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(e, value: { id: number; label: string } | null) =>
                    handleChange("created_by", value?.id)
                  }
                  renderInput={(params) => (
                    <TextField {...params} required label="Usuario" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  data-cy="author"
                  id="author"
                  autoComplete
                  disableClearable
                  value={
                    users.find((u) => u.id === movement.updated_by)
                      ? {
                          id: users.find((u) => u.id === movement.updated_by)
                            ?.id!,
                          label: `${
                            users.find((u) => u.id === movement.updated_by)
                              ?.first_name
                          } ${
                            users.find((u) => u.id === movement.updated_by)
                              ?.last_name
                          }`,
                        }
                      : undefined
                  }
                  options={filteredUsers}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  disabled={!movement.created_by}
                  onChange={(e, value: { id: number; label: string } | null) =>
                    handleChange("updated_by", value?.id)
                  }
                  renderInput={(params) => (
                    <TextField {...params} required label="Preparó" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} data-cy={option.label}>
                      {option.label}
                    </li>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <DateTimePicker
                  disableFuture
                  label="Fecha"
                  format="dd/MM/yyyy"
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                  slots={{
                    actionBar: DateTimePickerActionBar,
                  }}
                  slotProps={{
                    textField: {
                      inputProps: {
                        "data-cy": `date`,
                      },
                    },
                  }}
                  views={DATE_VIEWS}
                  value={movement.date ? new Date(movement.date) : null}
                  disabled={movement.isAdjustment && !!movement.movement_id}
                  onChange={(value: Date | null) => {
                    if (value?.toISOString()) {
                      handleChange("date", dateISOFormat(value.toISOString()));
                      handleChange(
                        "settlement_date",
                        dateISOFormat(value.toISOString())
                      );
                    }
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  data-cy="client"
                  id="client"
                  disableClearable
                  autoComplete
                  value={
                    clients.find((c) => c.id === movement.client_id)
                      ? {
                          id: clients.find((c) => c.id === movement.client_id)
                            ?.id!,
                          label: clients.find(
                            (c) => c.id === movement.client_id
                          )?.razon_social!,
                        }
                      : undefined
                  }
                  options={clients.filter((c) => c.parent_client_id == null).map((c) => ({
                    id: c.id,
                    label: `${c.code} - ${c.razon_social}`,
                  }))}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(
                    e,
                    value: {
                      id: number;
                      label: string;
                    } | null
                  ) => {
                    setMovement((prev) => ({
                      ...prev,
                      client_id: value?.id ?? 0,
                      associated_client: "",
                      articles: [],
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} required label="Cliente" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} data-cy={option.label}>
                      {option.label}
                    </li>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  data-cy="associated-client"
                  id="associated-client"
                  disableClearable
                  options={clientAssociatedClients.map(
                    (ac: AssociatedClient) => ({
                      label: ac.razon_social,
                    })
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  value={{ label: movement.associated_client }}
                  disabled={!movement.client_id}
                  onChange={(e, value: { label: string } | null) =>
                    handleChange("associated_client", value?.label)
                  }
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} required label="Cliente Asociado" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} data-cy={option.label}>
                      {option.label}
                    </li>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth required margin="none">
                <TextField
                  data-cy="income-client-doc"
                  label="Documento del Cliente"
                  value={movement.client_document}
                  onChange={(e) => handleChange("client_document", e.target.value)}
                  fullWidth
                  required
                />
              </FormControl>  
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <Autocomplete
                  data-cy="warehouse"
                  id="warehouse"
                  autoComplete
                  value={selectedWarehouse}
                  options={storesList}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(
                    e,
                    value: { id: number; label: string } | null
                  ) => {
                    setMovement((prev) => ({
                      ...prev,
                      store_id: value?.id || null,
                      articles: [],
                    }));
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} required label="Almacen" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} data-cy={option.label}>
                      {option.label}
                    </li>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <CustomNumberInput
                  label="Pallets"
                  value={movement.pallets}
                  onChange={(e) =>
                    handleChange("pallets", Number(e.target.value))
                  }
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <CustomNumberInput
                  label="Peso"
                  value={movement.weight}
                  onChange={(e) =>
                    handleChange("weight", Number(e.target.value))
                  }
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="none">
                <CustomNumberInput
                  data-cy="remito"
                  label="Número de Remito"
                  value={movement.numero_remito}
                  onChange={(e) =>
                    handleChange("numero_remito", e.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                data-cy="obs"
                id="observations"
                label="Observaciones"
                type="text"
                fullWidth
                value={movement.observation}
                onChange={(e) => handleChange("observation", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                Artículos
              </Typography>
            </Grid>
            <Grid item xs={12} my={2}>
              <ArticlesList
                articles={clientArticles}
                movement={movement}
                onMovementUpdate={handleChange}
                operationType={MOVEMENT_TYPES.INCOME.label}
                locations={locations}
                stores={stores}
                hasPalletAutocalculation={true}
                hasWeightAutocalculation={true}
                rowIndex={articleRowIndex}
                setRowIndex={setArticleRowIndex}
                client={selectedClient}
                sublocations={sublocations}
              />

              <Grid container spacing={1} sx={{ mt: 1 }} justifyContent="flex-start">
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Cantidad Total"
                    value={String(movementTotals.totalCantidad)}
                    fullWidth
                    disabled
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Total Unidad"
                    value={String(movementTotals.totalSubtotalUnidad)}
                    fullWidth
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              {!movement.isAdjustment && (
                <Button
                  data-cy="draft-income-button"
                  variant="contained"
                  disabled={submitDisabled}
                  onClick={() => handleSendDraft()}
                  sx={{ marginRight: 2 }}
                >
                  Guardar Borrador
                </Button>
              )}
              <Button
                data-cy="confirm-income-button"
                type="submit"
                variant="contained"
                disabled={submitDisabled}
              >
                Guardar Ingreso
              </Button>
            </Grid>
          </Grid>
        )}
      </form>
      <MovementAdditionalItemsStepModal
        open={additionalItemsModalOpen}
        title={getAdditionalItemsModalTitle(movement)}
        movement={movement}
        additionalItems={clientAdditionalItems}
        childClients={childClients}
        associatedClients={associatedClients}
        initialItems={additionalItemsInitialItems}
        onClose={() => {
          setAdditionalItemsModalOpen(false);
          setAdditionalItemsInitialItems([]);
        }}
        onConfirm={handleConfirmAdditionalItems}
      />
    </PageLayout>
  );
}
